import { clsx } from 'clsx';

/**
 * Shot streak dots for a drill or a Lab round. Made is a filled mint dot with a check, missed a
 * peach dot with a cross, still to shoot a dashed empty dot, so it reads without color. The
 * current shot gets a heavier ring.
 *
 *   <Dots results={['made', 'missed', 'made']} total={5} />   ✓ ✗ ✓ ◌ ◌
 */
export type DotResult = 'made' | 'missed' | null;

export interface DotsProps {
  results: readonly DotResult[];
  /** Total slots; extra slots render as still to shoot. Default results.length. */
  total?: number;
  size?: 'sm' | 'md';
  /** Mark the next empty slot as the current shot. Default true. */
  showCurrent?: boolean;
  label?: string;
  className?: string;
}

export function dotsSummary(results: readonly DotResult[], total: number): string {
  const made = results.filter((r) => r === 'made').length;
  const missed = results.filter((r) => r === 'missed').length;
  const left = Math.max(0, total - made - missed);
  return `${made} made, ${missed} missed${left ? `, ${left} to go` : ''}`;
}

export function Dots({ results, total, size = 'md', showCurrent = true, label, className }: DotsProps) {
  const n = Math.max(total ?? results.length, results.length);
  const firstEmpty = results.length < n ? results.length : results.indexOf(null);
  const box = size === 'sm' ? 'size-5 text-[10px]' : 'size-7 text-sm';
  return (
    <span
      role="img"
      aria-label={label ? `${label}: ${dotsSummary(results, n)}` : dotsSummary(results, n)}
      className={clsx('inline-flex flex-wrap items-center gap-1.5', className)}
    >
      {Array.from({ length: n }, (_, i) => {
        const r = results[i] ?? null;
        const current = showCurrent && i === firstEmpty;
        return (
          <span
            // biome-ignore lint/suspicious/noArrayIndexKey: slots are positional and never reorder
            key={i}
            aria-hidden="true"
            className={clsx(
              'grid place-items-center rounded-full border-ink font-black leading-none text-ink',
              box,
              r === 'made' && 'border-2 bg-mint',
              r === 'missed' && 'border-2 bg-peach',
              r === null && (current ? 'border-3 bg-butter' : 'border-2 border-dashed bg-white'),
            )}
          >
            {r === 'made' ? '✓' : r === 'missed' ? '✗' : ''}
          </span>
        );
      })}
    </span>
  );
}
